import { useEffect, useState, useRef, useCallback, useMemo, memo } from "react";
import { useQueue } from "../Context/QueueProvider.tsx";
import { usePlayerRef } from "../Context/PlayerRefProvider.tsx";
import { colors } from "../styles/theme.js";
import useUrlParams from "../Hooks/useUrlParams.tsx";
import { useMountTimestamp } from "../Context/MountTimestampProvider.tsx";

const VIDEO_STATE = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  VIDEO_CUED: 5,
};

export function FrameManager({ style, muted = false }) {
  const {
    primaryTimestamp,
    playlist,
    currentVideoTitle,
    incrementPlayingIndex,
    isLastVideo,
  } = useQueue();

  return (
    <>
      <FramePlayer
        playlist={playlist}
        initialPlayTime={primaryTimestamp}
        onVideoEnded={incrementPlayingIndex}
        isLastVideo={isLastVideo}
        style={style}
        videoTitle={currentVideoTitle}
        muted={muted}
      />
    </>
  );
}

const FramePlayer = ({
  initialPlayTime,
  onVideoEnded,
  playlist,
  style,
  videoTitle,
  hideOverlay = false,
  muted,
}) => {
  const { volume: volumeParam } = useUrlParams(["volume"]);

  const { playerRef, setIsLoading, isLoading } = usePlayerRef();
  const mountTimestampRef = useMountTimestamp();

  const isLoadingRef = useRef(isLoading);
  const hasSeenInitialPlaying = useRef(false);

  const lastIndexRef = useRef(0);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  console.log(
    playerRef.current?.getPlaylistIndex(),
    playerRef.current?.getPlaylist().length - 1
  );

  const onError = useCallback(
    (err) => {
      if (err.data === 101 || err.data === 150) {
        onVideoEnded();
      }
      // TODO report error
    },
    [onVideoEnded]
  );

  const onStateChange = useCallback(
    (event) => {
      // if (
      //   playerRef.current.getPlaylistIndex() ===
      //   playerRef.current.getPlaylist().length - 1
      // ) {
      //   bindPlayer(playlist.join(","));
      // }

      if (event.data === VIDEO_STATE.PLAYING) {
        if (lastIndexRef.current !== playerRef.current.getPlaylistIndex()) {
          onVideoEnded();
          lastIndexRef.current = playerRef.current.getPlaylistIndex();
        }
      }
    },
    [onVideoEnded, playerRef]
  );

  const startPlayer = useCallback(() => {
    setTimeout(() => {
      playerRef.current?.setVolume && playerRef.current?.setVolume(0);
    }, 1000);

    setTimeout(() => {
      const loadingTime = (Date.now() - mountTimestampRef.current) / 1000;
      console.log("starting", loadingTime);

      playerRef.current?.seekTo &&
        playerRef.current?.seekTo(initialPlayTime + loadingTime, true);

      if (!muted) {
        playerRef.current?.setVolume &&
          playerRef.current?.setVolume(volumeParam || 50);
      }

      if (!!playerRef.current) {
        playerRef.current?.playVideo();
        setIsLoading(false);
      }
    }, 3000);
  }, [
    initialPlayTime,
    mountTimestampRef,
    muted,
    playerRef,
    setIsLoading,
    volumeParam,
  ]);

  const bindPlayer = useCallback(
    (test) => {
      console.log({ test });
      const newPlayerConfig = {
        height: "390",
        width: "640",
        playerVars: {
          listType: "playlist",
          playlist: test,
          muted: 1,
          controls: 0,
          rel: 0,
          loop: 1,
        },
        events: {
          onStateChange: onStateChange,
          onError: onError,
        },
      };

      playerRef.current = new window.YT.Player(
        "youtube-player",
        newPlayerConfig
      );

      startPlayer();
    },
    [onError, onStateChange, playerRef, startPlayer]
  );

  useEffect(() => {
    if (playlist.length === 0 || !!playerRef.current) return;

    // Reset flags when creating new player
    hasSeenInitialPlaying.current = false;

    bindPlayer(playlist?.join(","));

    startPlayer();
  }, [
    bindPlayer,
    initialPlayTime,
    muted,
    onStateChange,
    playerRef,
    playlist,
    setIsLoading,
    startPlayer,
    volumeParam,
  ]);

  return (
    <div>
      {hideOverlay && <VideoOverlay ref={playerRef} videoName={videoTitle} />}
      <PlayerComponent style={style} />
    </div>
  );
};

const PlayerComponent = memo(({ style }) => {
  return (
    <div>
      <div
        style={{
          ...style,
        }}
        id="youtube-player"
      />
    </div>
  );
});

export default FramePlayer;

const VideoOverlay = ({ ref, videoName }) => {
  const colorParam = "blue";
  const [hovered, setHovered] = useState(false);

  const hoverTimeoutRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    setHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    clearTimeout(hoverTimeoutRef.current);
  }, []);

  const videoNameTruncated = useMemo(() => {
    if (videoName.length <= 70) return videoName;
    return videoName.slice(0, 70) + "...";
  }, [videoName]);

  return (
    <div
      style={{
        position: "absolute",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        zIndex: 20,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        style={{
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.2s ease-in-out",
          position: "absolute",
          width: "100%",
          height: "100%",
          top: 0,
          left: 0,
          background: `rgba(0,0,0,0.75)`,
          borderRadius: 8,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 21,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            width: "80%",
            height: "50%",
          }}
        >
          <h1
            style={{
              fontSize: 40,
              color: colorParam || colors.white,
              fontFamily: "Pixelify Sans",
              textAlign: "center",
            }}
          >
            {videoNameTruncated}
          </h1>
        </div>
      </div>
    </div>
  );
};
